import { useCallback, useEffect, useState } from "react";
import { EyeOff, Loader2, Lock, Plus, ShieldCheck, UserCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, shortDate } from "@/lib/format";

const B_BLANK = {
  code: "", display_name: "", pseudonym: "", program_code: "", location: "", country: "IN",
  age_band: "10-14", household_size: 1, eligibility_criteria: [], data_class: "personal",
  identity_reference: "", vulnerability_flags: [], safeguarding_restricted: false,
  status: "active", notes: "",
};

const C_BLANK = {
  beneficiary_id: "", title: "", case_type: "service_delivery", severity: "medium",
  owner: "", safeguarding_restricted: false, summary: "",
};

const SEVERITY_STYLE = {
  critical: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  high: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  medium: "border-primary/40 bg-primary/10 text-primary",
  low: "border-border bg-muted text-muted-foreground",
};

const CASE_STATUS_STYLE = {
  open: "border-primary/40 bg-primary/10 text-primary",
  in_progress: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  escalated: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  closed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

export default function Beneficiaries() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const rowDetail = useRowDetail();
  const [cases, setCases] = useState({ items: [], safeguarding_access: false, restricted_count: 0 });
  const [bForm, setBForm] = useState(B_BLANK);
  const [cForm, setCForm] = useState(C_BLANK);
  const [criteria, setCriteria] = useState("");
  const [flags, setFlags] = useState("");
  const [bOpen, setBOpen] = useState(false);
  const [cOpen, setCOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [consentScope, setConsentScope] = useState("programme_delivery, impact_reporting");
  const [consentBasis, setConsentBasis] = useState("explicit_consent");
  const [entryText, setEntryText] = useState("");

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/beneficiaries"), api.get("/beneficiaries/cases/all")]);
    setData(b.data);
    setCases(c.data);
  }, []);

  useEffect(() => { load().catch(() => setData({ items: [], safeguarding_access: false, restricted_count: 0 })); }, [load]);

  const saveBeneficiary = async () => {
    setBusy("b");
    try {
      await api.post("/beneficiaries", {
        ...bForm,
        eligibility_criteria: criteria.split("\n").map((s) => s.trim()).filter(Boolean)
          .map((c) => ({ criterion: c.replace(/^\[x\]\s*/i, ""), met: /^\[x\]/i.test(c) })),
        vulnerability_flags: flags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Beneficiary registered with eligibility assessment");
      setBOpen(false);
      setBForm(B_BLANK);
      setCriteria("");
      setFlags("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveCase = async () => {
    setBusy("c");
    try {
      await api.post("/beneficiaries/cases", cForm);
      toast.success("Case opened");
      setCOpen(false);
      setCForm(C_BLANK);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const openDetail = async (id) => {
    try {
      const { data: d } = await api.get(`/beneficiaries/${id}`);
      setDetail(d);
    } catch (e) { toast.error(apiError(e)); }
  };

  const captureConsent = async (withdraw) => {
    setBusy("consent");
    try {
      await api.post(`/beneficiaries/${detail.beneficiary.id}/consent`, {
        lawful_basis: consentBasis,
        scope: consentScope.split(",").map((s) => s.trim()).filter(Boolean),
        captured_by_channel: "in_person",
        evidence_note: withdraw ? "Withdrawal recorded" : "Consent form captured and filed",
        withdraw,
      });
      toast.success(withdraw ? "Consent withdrawal recorded" : "Consent captured with lawful basis");
      await openDetail(detail.beneficiary.id);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const addEntry = async (caseId) => {
    if (!entryText.trim()) return;
    setBusy(caseId);
    try {
      await api.post(`/beneficiaries/cases/${caseId}/entries`, { text: entryText, action: "note" });
      setEntryText("");
      toast.success("Case note added to the audit trail");
      await openDetail(detail.beneficiary.id);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const setCaseStatus = async (caseId, status) => {
    setBusy(caseId);
    try {
      await api.post(`/beneficiaries/cases/${caseId}/status`, { status, note: "" });
      toast.success(`Case marked ${status.replace(/_/g, " ")}`);
      if (detail) await openDetail(detail.beneficiary.id);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading beneficiary registry" />;
  const items = data.items;
  const withConsent = items.filter((b) => b.consent?.given).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-010 · Beneficiary & Case Management"
        title="Serve people, protect their data"
        description="Eligibility assessment, lawful-basis consent capture and case work. Safeguarding-restricted records are redacted for roles without safeguarding access, and every access attempt is audited."
        actions={
          <div className="flex flex-wrap gap-2">
            {can("beneficiary", "write") && (
              <Dialog open={cOpen} onOpenChange={setCOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="new-case-button" className="rounded-full border-border text-xs">
                    <Plus className="mr-1.5 h-3 w-3" /> Open case
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md border-border bg-card">
                  <DialogHeader>
                    <DialogTitle>Open a case</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">
                      Sensitive-data beneficiaries require captured consent before a case can be opened (FR-PGM-003).
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <F label="Beneficiary">
                      <Select value={cForm.beneficiary_id} onValueChange={(v) => setCForm({ ...cForm, beneficiary_id: v })}>
                        <SelectTrigger data-testid="case-beneficiary-select" className="bg-muted/40">
                          <SelectValue placeholder="Select beneficiary" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.code} · {b.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Title">
                      <Input value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })}
                             data-testid="case-title-input" className="bg-muted/40" />
                    </F>
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Case type">
                        <Select value={cForm.case_type} onValueChange={(v) => setCForm({ ...cForm, case_type: v })}>
                          <SelectTrigger data-testid="case-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["service_delivery", "health_referral", "safeguarding", "grievance", "eligibility_review"].map((t) => (
                              <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </F>
                      <F label="Severity">
                        <Select value={cForm.severity} onValueChange={(v) => setCForm({ ...cForm, severity: v })}>
                          <SelectTrigger data-testid="case-severity-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["low", "medium", "high", "critical"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </F>
                    </div>
                    <F label="Summary">
                      <Textarea rows={2} value={cForm.summary} onChange={(e) => setCForm({ ...cForm, summary: e.target.value })}
                                data-testid="case-summary-input" className="bg-muted/40 text-xs" />
                    </F>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Safeguarding restricted</span>
                      <Switch checked={cForm.safeguarding_restricted}
                              onCheckedChange={(v) => setCForm({ ...cForm, safeguarding_restricted: v })}
                              data-testid="case-restricted-toggle" />
                    </div>
                  </div>
                  <Button onClick={saveCase} disabled={busy === "c" || !cForm.beneficiary_id || !cForm.title}
                          data-testid="case-save-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy === "c" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Open case
                  </Button>
                </DialogContent>
              </Dialog>
            )}
            {can("beneficiary", "write") && (
              <Dialog open={bOpen} onOpenChange={setBOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-beneficiary-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Plus className="mr-1.5 h-4 w-4" /> Register beneficiary
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto border-border bg-card">
                  <DialogHeader>
                    <DialogTitle>Register a beneficiary</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">
                      Eligibility is derived from the criteria you record. Prefix a criterion with [x] to mark it met.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Code">
                      <Input value={bForm.code} onChange={(e) => setBForm({ ...bForm, code: e.target.value.toUpperCase() })}
                             data-testid="beneficiary-code-input" className="bg-muted/40 font-mono" />
                    </F>
                    <F label="Display name">
                      <Input value={bForm.display_name} onChange={(e) => setBForm({ ...bForm, display_name: e.target.value })}
                             data-testid="beneficiary-name-input" className="bg-muted/40" />
                    </F>
                    <F label="Programme code">
                      <Input value={bForm.program_code} onChange={(e) => setBForm({ ...bForm, program_code: e.target.value.toUpperCase() })}
                             data-testid="beneficiary-program-input" className="bg-muted/40 font-mono" />
                    </F>
                    <F label="Location">
                      <Input value={bForm.location} onChange={(e) => setBForm({ ...bForm, location: e.target.value })}
                             data-testid="beneficiary-location-input" className="bg-muted/40" />
                    </F>
                    <F label="Age band">
                      <Select value={bForm.age_band} onValueChange={(v) => setBForm({ ...bForm, age_band: v })}>
                        <SelectTrigger data-testid="beneficiary-ageband-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["00-04", "05-09", "10-14", "15-19", "20-24", "25-34", "35-49", "50-64", "65+", "unknown"].map((a) => (
                            <SelectItem key={a} value={a}>{a}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Data class">
                      <Select value={bForm.data_class} onValueChange={(v) => setBForm({ ...bForm, data_class: v })}>
                        <SelectTrigger data-testid="beneficiary-dataclass-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["none", "personal", "sensitive"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Eligibility criteria (one per line, [x] = met)" full>
                      <Textarea rows={3} value={criteria} onChange={(e) => setCriteria(e.target.value)}
                                data-testid="beneficiary-criteria-input" className="bg-muted/40 font-mono text-[11px]"
                                placeholder="[x] Out of school > 6 months&#10;Household below poverty line" />
                    </F>
                    <F label="Vulnerability flags (comma separated)" full>
                      <Input value={flags} onChange={(e) => setFlags(e.target.value)}
                             data-testid="beneficiary-flags-input" className="bg-muted/40 font-mono text-xs" />
                    </F>
                    <div className="col-span-2 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Safeguarding restricted record</span>
                      <Switch checked={bForm.safeguarding_restricted}
                              onCheckedChange={(v) => setBForm({ ...bForm, safeguarding_restricted: v })}
                              data-testid="beneficiary-restricted-toggle" />
                    </div>
                  </div>
                  <Button onClick={saveBeneficiary} disabled={busy === "b" || !bForm.code || !bForm.display_name}
                          data-testid="beneficiary-save-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy === "b" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Register
                  </Button>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-beneficiaries" label="Beneficiaries" value={items.length} accent="sky" icon={UserCheck} />
        <Kpi testId="kpi-consent" label="Consent captured" accent="emerald" icon={ShieldCheck}
             value={`${withConsent}/${items.length}`} sub="Lawful basis on file" />
        <Kpi testId="kpi-cases-open" label="Open cases" accent="violet"
             value={cases.items.filter((c) => c.status !== "closed").length}
             sub={`${cases.items.length} total`} />
        <Kpi testId="kpi-restricted" label="Safeguarding restricted" accent="red" icon={Lock}
             value={data.restricted_count}
             sub={data.safeguarding_access ? "You have safeguarding access" : "Redacted for your role"} />
      </div>

      {!data.safeguarding_access && data.restricted_count > 0 && (
        <div className="flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300"
             data-testid="safeguarding-notice">
          <EyeOff className="h-4 w-4 shrink-0" />
          {data.restricted_count} record(s) are safeguarding-restricted and shown redacted. Access is limited to
          safeguarding leads (FR-PGM-004) and every attempt is written to the audit log.
        </div>
      )}

      <Tabs defaultValue="beneficiaries">
        <TabsList className="bg-muted/40">
          <TabsTrigger value="beneficiaries" data-testid="tab-beneficiaries">Beneficiary registry</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-cases">Case log</TabsTrigger>
        </TabsList>

        <TabsContent value="beneficiaries" className="mt-4">
          <Panel testId="panel-beneficiaries" kicker="FR-PGM-003" title="Registry">
            <Table testId="beneficiaries-table"
                   head={<><Th>Code</Th><Th>Beneficiary</Th><Th>Programme</Th><Th>Location</Th><Th>Data class</Th><Th>Eligibility</Th><Th>Consent</Th><Th>Access</Th></>}>
              {items.length === 0 && <Empty colSpan={8} label="No beneficiaries registered" />}
              {items.map((b) => (
                <tr key={b.id} className="row-hover cursor-pointer" data-testid={`beneficiary-row-${b.code}`}
                    onClick={() => openDetail(b.id)}>
                  <Td className="font-mono text-[11px] text-primary">{b.code}</Td>
                  <Td>
                    <div className={`font-medium ${b.redacted ? "text-amber-700 dark:text-amber-300" : "text-foreground"}`}>{b.display_name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground/70">{b.pseudonym}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">{b.program_code || "—"}</Td>
                  <Td className="text-[11px] text-muted-foreground">{b.location || "—"}</Td>
                  <Td>
                    <Chip className={b.data_class === "sensitive"
                      ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                      : "border-border bg-muted text-foreground/80"}>{b.data_class}</Chip>
                  </Td>
                  <Td>
                    <Chip className={b.eligibility_status === "eligible"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : b.eligibility_status === "not_eligible"
                        ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                        : "border-border bg-muted text-muted-foreground"}
                          testId={`eligibility-${b.code}`}>
                      {b.eligibility_status.replace(/_/g, " ")}
                    </Chip>
                  </Td>
                  <Td>
                    {b.consent?.given
                      ? <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">{b.consent.lawful_basis}</Chip>
                      : <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">not captured</Chip>}
                  </Td>
                  <Td>
                    {b.safeguarding_restricted
                      ? <Chip testId={`restricted-${b.code}`} className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300">
                          <Lock className="h-3 w-3" /> restricted
                        </Chip>
                      : <span className="font-mono text-[10px] text-muted-foreground/70">standard</span>}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="cases" className="mt-4">
          <Panel testId="panel-cases-log" kicker="FR-PGM-004" title="Cases">
            <Table testId="cases-log-table"
                   head={<><Th>Case</Th><Th>Beneficiary</Th><Th>Type</Th><Th>Severity</Th><Th>Opened</Th><Th>Status</Th><Th /></>}>
              {cases.items.length === 0 && <Empty colSpan={7} label="No cases" />}
              {cases.items.map((c) => (
                <tr key={refOf(c)} className="row-hover cursor-pointer" onClick={rowClick(() => rowDetail.show(c, refOf(c)))} data-testid={`case-row-${c.case_number}`}>
                  <Td>
                    <div className="font-mono text-[11px] text-primary">{c.case_number}</div>
                    <div className={`text-[11px] ${c.redacted ? "text-amber-700 dark:text-amber-300" : "text-foreground/80"}`}>{c.title}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">{c.beneficiary_code}</Td>
                  <Td className="text-[11px] text-muted-foreground">{c.case_type.replace(/_/g, " ")}</Td>
                  <Td><Chip className={SEVERITY_STYLE[c.severity]}>{c.severity}</Chip></Td>
                  <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(c.opened_on)}</Td>
                  <Td>
                    <Chip className={CASE_STATUS_STYLE[c.status]} testId={`case-status-${c.case_number}`}>
                      {c.status.replace(/_/g, " ")}
                    </Chip>
                  </Td>
                  <Td>
                    {c.status !== "closed" && can("beneficiary", "write") && !c.redacted && (
                      <Button size="sm" variant="outline" onClick={() => setCaseStatus(c.id, "closed")}
                              disabled={busy === c.id} data-testid={`close-case-${c.case_number}`}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {busy === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Close"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-xl">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground" data-testid="beneficiary-detail-name">
                  {detail.beneficiary.display_name}
                </SheetTitle>
              </SheetHeader>

              {detail.beneficiary.redacted && (
                <div className="mt-4 flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300"
                     data-testid="detail-redacted-notice">
                  <Lock className="h-4 w-4 shrink-0" /> This record is safeguarding-restricted; identifying
                  details and case notes are withheld from your role.
                </div>
              )}

              <div className="mt-4 space-y-1.5 rounded border border-border p-3 text-xs">
                <Line k="Code" v={detail.beneficiary.code} />
                <Line k="Programme" v={detail.beneficiary.program_code || "—"} />
                <Line k="Location" v={detail.beneficiary.location || "—"} />
                <Line k="Age band" v={detail.beneficiary.age_band} />
                <Line k="Data class" v={detail.beneficiary.data_class} />
                <Line k="Eligibility" v={detail.beneficiary.eligibility_status} />
                <Line k="Flags" v={(detail.beneficiary.vulnerability_flags || []).join(", ") || "—"} />
              </div>

              {(detail.beneficiary.eligibility_criteria || []).length > 0 && (
                <div className="mt-4">
                  <div className="kicker mb-2">Eligibility assessment</div>
                  <div className="space-y-1">
                    {detail.beneficiary.eligibility_criteria.map((c) => (
                      <div key={c.criterion} className="flex items-center justify-between rounded border border-border px-2.5 py-1.5 text-[11px]">
                        <span className="text-foreground/80">{c.criterion}</span>
                        <Chip className={c.met
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>{c.met ? "met" : "not met"}</Chip>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 rounded border border-border p-3">
                <div className="kicker mb-2">Consent & lawful basis</div>
                {detail.beneficiary.consent?.given ? (
                  <div className="space-y-1 text-[11px]">
                    <Line k="Basis" v={detail.beneficiary.consent.lawful_basis} />
                    <Line k="Scope" v={(detail.beneficiary.consent.scope || []).join(", ")} />
                    <Line k="Channel" v={detail.beneficiary.consent.channel} />
                    <Line k="Captured by" v={detail.beneficiary.consent.captured_by} />
                    <Line k="Captured at" v={dateTime(detail.beneficiary.consent.captured_at)} />
                  </div>
                ) : (
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">No consent on file. Sensitive-data cases are blocked until captured.</p>
                )}

                {can("beneficiary", "consent") && (
                  <div className="mt-3 space-y-2 border-t border-border pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      <F label="Lawful basis">
                        <Select value={consentBasis} onValueChange={setConsentBasis}>
                          <SelectTrigger data-testid="consent-basis-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["explicit_consent", "guardian_consent", "legal_obligation", "vital_interests", "public_task"].map((b) => (
                              <SelectItem key={b} value={b}>{b.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </F>
                      <F label="Scope (comma separated)">
                        <Input value={consentScope} onChange={(e) => setConsentScope(e.target.value)}
                               data-testid="consent-scope-input" className="h-8 bg-muted/40 font-mono text-[11px]" />
                      </F>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => captureConsent(false)} disabled={busy === "consent"}
                              data-testid="capture-consent-button"
                              className="rounded-full bg-emerald-500/90 text-xs text-white hover:bg-emerald-400">
                        {busy === "consent" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3 w-3" />}
                        Capture consent
                      </Button>
                      {detail.beneficiary.consent?.given && (
                        <Button size="sm" variant="outline" onClick={() => captureConsent(true)} disabled={busy === "consent"}
                                data-testid="withdraw-consent-button" className="rounded-full border-border text-xs">
                          Record withdrawal
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="kicker mb-2">Cases</div>
                <div className="space-y-2">
                  {detail.cases.length === 0 && <div className="text-xs text-muted-foreground/70">No cases for this beneficiary</div>}
                  {detail.cases.map((c) => (
                    <div key={c.id} className="rounded border border-border p-3" data-testid={`detail-case-${c.case_number}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="font-mono text-[11px] text-primary">{c.case_number}</div>
                          <div className="text-xs text-foreground">{c.title}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Chip className={SEVERITY_STYLE[c.severity]}>{c.severity}</Chip>
                          <Chip className={CASE_STATUS_STYLE[c.status]}>{c.status.replace(/_/g, " ")}</Chip>
                        </div>
                      </div>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">{c.summary}</p>
                      <div className="mt-2 space-y-1">
                        {(c.entries || []).map((e, i) => (
                          <div key={i} className="rounded bg-muted/40 px-2 py-1.5 text-[11px]">
                            <div className="text-foreground/80">{e.text}</div>
                            <div className="font-mono text-[10px] text-muted-foreground/70">{e.by} · {dateTime(e.at)} · {e.action}</div>
                          </div>
                        ))}
                      </div>
                      {!c.redacted && c.status !== "closed" && can("beneficiary", "write") && (
                        <div className="mt-2 flex gap-2">
                          <Input value={entryText} onChange={(e) => setEntryText(e.target.value)}
                                 placeholder="Add a case note" data-testid={`case-entry-input-${c.case_number}`}
                                 className="h-8 bg-muted/40 text-xs" />
                          <Button size="sm" onClick={() => addEntry(c.id)} disabled={busy === c.id}
                                  data-testid={`case-entry-save-${c.case_number}`}
                                  className="h-8 rounded-full bg-primary px-3 text-[11px] text-white hover:bg-primary/90">
                            Add
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {rowDetail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Line = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-right text-foreground/80">{String(v)}</span>
  </div>
);
